import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** Diameter + initials type per size. xs 20 (dense table cells) · sm 24 (pickers, timelines) · md 32 (top bar) · lg 40 · xl 96 (profile). */
const SIZE: Record<AvatarSize, string> = {
  xs: 'size-5 text-[9px]',
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
  xl: 'size-24 text-[32px]',
};

/** Number of muted tones defined as --avatar-N-bg / --avatar-N-fg in styles/index.css (light + dark). */
export const AVATAR_TONES = 8;

const stripMarks = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC');

/**
 * Initials for Vietnamese names: first letter of the first word + first letter of the last word (the given name),
 * e.g. "Nguyễn Khắc Cao" → "NC", "Trần Thị Thu Hà" → "TH", "Cao" → "C". Tone marks are dropped ("Ánh" → "A"), Đ is kept.
 * Words that don't start with a letter ("(DEMO)", "-") are ignored; an email falls back to its local part.
 */
export function initialsOf(name: string | null | undefined): string {
  const raw = (name ?? '').trim();
  const base = raw.includes('@') && !raw.includes(' ') ? raw.split('@')[0]!.replace(/[._-]+/g, ' ') : raw;
  const words = base.split(/\s+/).filter((w) => /^\p{L}/u.test(w));
  if (words.length === 0) return '';
  const first = words[0]!;
  const last = words[words.length - 1]!;
  const letter = (w: string) => stripMarks(Array.from(w)[0] ?? '').toLocaleUpperCase('vi');
  return words.length === 1 ? letter(first) : letter(first) + letter(last);
}

/** Stable tone 1…AVATAR_TONES from the user id (or the name when no id): the same person keeps the same colour everywhere. */
export function avatarTone(seed: number | string | null | undefined): number {
  if (typeof seed === 'number' && Number.isFinite(seed)) return (Math.abs(Math.trunc(seed)) % AVATAR_TONES) + 1;
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return (Math.abs(h) % AVATAR_TONES) + 1;
}

export interface AvatarProps {
  /** Full name: initials, alt text and (without `id`) the colour seed. */
  name: string | null | undefined;
  /** Signed avatar URL from the API (`avatarUrl`); null/undefined → initials. */
  src?: string | null;
  /** User id: colour seed (preferred over the name, so renames keep the colour). */
  id?: number | null;
  size?: AvatarSize;
  /** Next to a visible name: hidden from assistive tech (the name is already read). Default true. */
  decorative?: boolean;
  className?: string;
  /** Shown instead of initials when there is no name (e.g. a generic person icon). */
  fallback?: ReactNode;
}

/**
 * Round user avatar: the photo when there is one, otherwise initials on a muted, id-derived tone. A photo that fails to load
 * (deleted, expired URL, offline) falls back to the initials.
 */
export function Avatar({ name, src, id, size = 'sm', decorative = true, className, fallback }: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && failedSrc !== src;
  const initials = initialsOf(name);
  const tone = avatarTone(id ?? name ?? '');
  const label = name?.trim() || undefined;
  return (
    <span
      className={cn('relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold select-none', SIZE[size], className)}
      style={showImage ? undefined : { backgroundColor: `var(--avatar-${tone}-bg)`, color: `var(--avatar-${tone}-fg)` }}
      role={decorative ? undefined : 'img'}
      aria-label={decorative || showImage ? undefined : label}
      aria-hidden={decorative || undefined}
      title={decorative ? undefined : label}
    >
      {showImage ? (
        <img
          src={src!}
          alt={decorative ? '' : (label ?? '')}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="size-full object-cover"
          onError={() => setFailedSrc(src!)}
        />
      ) : (
        <span className="leading-none tracking-[0.02em]">{initials || fallback}</span>
      )}
      {/* Hairline so light photos and light tones keep an edge on white surfaces. */}
      <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-(--avatar-ring) ring-inset" aria-hidden />
    </span>
  );
}

/** Avatar + name (+ optional secondary line) in one aligned row — the standard way to show a person in tables and lists. */
export function PersonLabel({
  name,
  src,
  id,
  size = 'sm',
  secondary,
  className,
  nameClassName,
}: {
  name: ReactNode;
  src?: string | null;
  id?: number | null;
  size?: AvatarSize;
  secondary?: ReactNode;
  className?: string;
  nameClassName?: string;
}) {
  const text = typeof name === 'string' ? name : null;
  return (
    <span className={cn('inline-flex max-w-full min-w-0 items-center', size === 'xs' ? 'gap-1.5' : 'gap-2', className)}>
      <Avatar name={text} src={src} id={id} size={size} />
      <span className="min-w-0">
        <span className={cn('block truncate', nameClassName)} title={text ?? undefined}>
          {name}
        </span>
        {secondary && <span className="block truncate text-xs text-subtle">{secondary}</span>}
      </span>
    </span>
  );
}
